<template>
  <div class="qz-choice" :class="{ 'qz-answered': isAnswered }">
    <!-- eslint-disable vue/no-v-html -->
    <div class="qz-stem" v-html="view.stem"></div>
    <ul
      class="qz-options"
      :role="isMulti ? 'group' : 'radiogroup'"
      :aria-label="isMulti ? '多选题选项' : '单选题选项'"
    >
      <li
        v-for="(opt, index) in view.options"
        :key="opt.id"
        ref="options"
        class="qz-option"
        :class="optionClass(opt.id)"
        :role="isMulti ? 'checkbox' : 'radio'"
        :tabindex="optionTabIndex(index)"
        :aria-checked="selected.includes(opt.id) ? 'true' : 'false'"
        @click="handleSelect(opt.id)"
        @keydown="handleOptionKeydown($event, index, opt.id)"
      >
        <span
          class="qz-option-marker"
          :class="{ 'qz-option-marker--checkbox': isMulti }"
          :data-key="optionKey(index)"
          aria-hidden="true"
        ></span>
        <span class="qz-option-text" v-html="opt.text"></span>
      </li>
    </ul>
    <!-- eslint-enable vue/no-v-html -->
    <div v-if="isAnswered" class="qz-feedback" :class="feedbackStatusClass">
      <div v-if="feedback.right !== 0" class="qz-feedback-result">
        {{ feedback.right === 1 ? '回答正确' : '回答错误' }}
      </div>
      <div v-if="analysisText" class="qz-analysis" v-html="analysisText"></div>
    </div>
  </div>
</template>

<script>
// v-html 依赖说明：view.stem 与 options[].text（含 feedback.analysis.text）均为服务端
// BuildStudentView / 答后反馈接口下发，已经过 bluemonday 消毒（见 saas-api pkg/sanitize），
// 组件内 v-html 渲染是设计内行为。**学生自由输入文本永不走 v-html**（本组件不涉及自由文本题型）。
export default {
  name: 'ChoiceRenderer',
  props: {
    view: {
      type: Object,
      required: true
    },
    session: {
      type: Object,
      default: null
    },
    env: {
      type: Object,
      required: true
    },
    feedback: {
      type: Object,
      default: null
    },
    // 宿主透传条目的 type_key：multi_choice 走多选(增删)，single_choice/judgment 走单选(替换)
    typeKey: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      selected: this.restoreSelected()
    }
  },
  computed: {
    isMulti () {
      return this.typeKey === 'multi_choice'
    },
    isAnswered () {
      return !!this.feedback
    },
    correctIds () {
      if (!this.isAnswered || this.feedback.right === 0) return []
      return (this.feedback.correct_view && this.feedback.correct_view.optionIds) || []
    },
    analysisText () {
      return this.feedback && this.feedback.analysis && this.feedback.analysis.text
    },
    feedbackStatusClass () {
      if (!this.isAnswered || this.feedback.right === 0) return ''
      return this.feedback.right === 1 ? 'qz-feedback-right' : 'qz-feedback-wrong'
    }
  },
  methods: {
    restoreSelected () {
      const ids = this.session && this.session.answer && this.session.answer.optionIds
      return Array.isArray(ids) ? [...ids] : []
    },
    optionClass (id) {
      const cls = {}
      if (this.selected.includes(id)) cls['qz-selected'] = true
      // right=0 是问答类 matcher=none 的兜底占位，choice 类不应出现；防御式处理为不做高亮
      if (this.isAnswered && this.feedback.right !== 0) {
        if (this.correctIds.includes(id)) {
          cls['qz-correct'] = true
        } else if (this.selected.includes(id)) {
          cls['qz-wrong'] = true
        }
      }
      return cls
    },
    optionKey (index) {
      return String.fromCharCode(65 + index)
    },
    optionTabIndex (index) {
      if (this.isAnswered) return -1
      // 多选的每个选项都是独立 checkbox，保留标准的逐项 Tab；单选遵循 radio 的 roving tabindex。
      if (this.isMulti) return 0
      const selectedIndex = this.view.options.findIndex(opt => opt.id === this.selected[0])
      return index === (selectedIndex === -1 ? 0 : selectedIndex) ? 0 : -1
    },
    focusOption (index) {
      const refs = this.$refs.options
      const options = Array.isArray(refs) ? refs : refs ? [refs] : []
      const option = options[index]
      if (option && typeof option.focus === 'function') option.focus()
    },
    moveSingleFocus (targetIndex) {
      const count = this.view.options.length
      if (!count) return
      const nextIndex = ((targetIndex % count) + count) % count
      const nextOption = this.view.options[nextIndex]
      this.handleSelect(nextOption.id)
      this.$nextTick(() => this.focusOption(nextIndex))
    },
    handleOptionKeydown (event, index, id) {
      if (this.isAnswered) return
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
        event.preventDefault()
        this.handleSelect(id)
        return
      }
      if (this.isMulti) return
      const lastIndex = this.view.options.length - 1
      let targetIndex = null
      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
        targetIndex = index + 1
      } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
        targetIndex = index - 1
      } else if (event.key === 'Home') {
        targetIndex = 0
      } else if (event.key === 'End') {
        targetIndex = lastIndex
      }
      if (targetIndex !== null) {
        event.preventDefault()
        this.moveSingleFocus(targetIndex)
      }
    },
    handleSelect (id) {
      if (this.isAnswered) return
      if (this.isMulti) {
        const idx = this.selected.indexOf(id)
        if (idx === -1) {
          this.selected = [...this.selected, id]
        } else {
          this.selected = this.selected.filter(x => x !== id)
        }
      } else {
        this.selected = [id]
      }
      this.$emit('session-changed', {
        answer: { optionIds: [...this.selected] },
        complete: this.selected.length > 0
      })
    }
  }
}
</script>

<style src="../styles/base.css"></style>

<style scoped>
.qz-choice {
  position: relative;
}

.qz-options {
  list-style: none;
  margin: 0;
  padding: 0;
}

.qz-option {
  display: flex;
  align-items: center;
  box-sizing: border-box;
  min-height: var(--qz-option-min-height, 0);
  padding: var(--qz-option-padding, 10px 12px);
  margin-bottom: 8px;
  border: 1px solid var(--qz-option-border, #dcdfe6);
  border-radius: var(--qz-option-radius, 6px);
  cursor: pointer;
  color: var(--qz-text, #303133);
  background: var(--qz-option-background, transparent);
  box-shadow: var(--qz-option-shadow, none);
  transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.qz-choice:not(.qz-answered) .qz-option:hover {
  border-color: var(--qz-primary, #409eff);
  background: var(--qz-primary-hover-bg, rgba(64, 158, 255, 0.05));
  box-shadow: 0 4px 10px var(--qz-primary-shadow, rgba(64, 158, 255, 0.08));
  transform: translateY(-1px);
}

.qz-choice:not(.qz-answered) .qz-option:focus-visible {
  outline: 3px solid var(--qz-primary-focus-ring, rgba(64, 158, 255, 0.25));
  outline-offset: 2px;
}

.qz-choice.qz-answered .qz-option {
  cursor: default;
  pointer-events: none;
}

.qz-option-marker {
  display: grid;
  flex: 0 0 20px;
  width: 20px;
  height: 20px;
  margin-right: 12px;
  place-items: center;
  border-radius: 50%;
  border: 1px solid #abc1c5;
  color: #6d858d;
  background: #fff;
  font-size: 10px;
  font-weight: 800;
  line-height: 1;
  transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease;
}

.qz-option-marker::after {
  content: attr(data-key);
}

.qz-option-marker--checkbox {
  border-radius: 6px;
}

.qz-option-text {
  flex: 1 1 auto;
}

.qz-option.qz-selected {
  border-color: var(--qz-primary, #409eff);
  background: var(--qz-primary-selected-bg, rgba(64, 158, 255, 0.09));
}

.qz-option.qz-selected .qz-option-marker {
  border-color: var(--qz-primary, #409eff);
  background: var(--qz-primary, #409eff);
  color: #fff;
}

.qz-option.qz-selected .qz-option-marker::after {
  content: '✓';
  font-size: 13px;
}

.qz-option.qz-correct {
  border-color: var(--qz-correct, #67c23a);
  background: rgba(103, 194, 58, 0.08);
}

.qz-option.qz-correct .qz-option-marker {
  border-color: var(--qz-correct, #67c23a);
  background: var(--qz-correct, #67c23a);
  color: #fff;
}

.qz-option.qz-correct .qz-option-marker::after {
  content: '✓';
  font-size: 13px;
}

.qz-option.qz-wrong {
  border-color: var(--qz-wrong, #f56c6c);
  background: rgba(245, 108, 108, 0.08);
}

.qz-option.qz-wrong .qz-option-marker {
  border-color: var(--qz-wrong, #f56c6c);
  background: var(--qz-wrong, #f56c6c);
  color: #fff;
}

.qz-option.qz-wrong .qz-option-marker::after {
  content: '×';
  font-size: 15px;
}

@media (prefers-reduced-motion: reduce) {
  .qz-option,
  .qz-option-marker {
    transition: none;
  }
}
</style>
